/**
 * Converts service pub cap percentage (integer format) to frontend percentage format
 * Backend stores percentages as integers multiplied by 100 (e.g., 10000 = 100%)
 * Frontend uses decimal percentages (e.g., 100 = 100%)
 * Caps the result at a maximum of 100% to prevent invalid backend data from causing issues
 *
 * @param servicePubCap - Service pub cap percentage (integer format)
 * @returns Frontend pub cap percentage (decimal format), capped at 100%
 *
 * @example
 * servicePubCapToPubCapPct(10000) // returns 100
 * servicePubCapToPubCapPct(9000)  // returns 90
 * servicePubCapToPubCapPct(15000) // returns 100 (capped)
 * servicePubCapToPubCapPct(undefined) // returns undefined
 */
export function servicePubCapToPubCapPct(servicePubCap) {
    if (servicePubCap === undefined || servicePubCap === null) {
        return undefined;
    }
    const converted = servicePubCap / 100;
    return Math.min(converted, 100);
}
/**
 * Converts service pub cap object to frontend percentage format with context-aware defaults
 *
 * @param pubCapObject - Service pub cap object that may be empty or contain pub_cap_pct
 * @returns Frontend pub cap percentage (decimal format) or undefined if no cap data
 *
 * @example
 * servicePubCapObjectToPubCapPct({pub_cap_pct: 10000}) // returns 100
 * servicePubCapObjectToPubCapPct({pub_cap_pct: null}) // returns 0 (null means 0% cap)
 * servicePubCapObjectToPubCapPct({}) // returns undefined (no pub_cap_pct)
 * servicePubCapObjectToPubCapPct(undefined) // returns undefined (no cap data)
 */
export function servicePubCapObjectToPubCapPct(pubCapObject) {
    if (!pubCapObject) {
        return undefined;
    }
    if (pubCapObject.pub_cap_pct === undefined) {
        return undefined;
    }
    // If pub_cap_pct is explicitly null, it means 0% cap
    if (pubCapObject.pub_cap_pct === null) {
        return 0;
    }
    const converted = pubCapObject.pub_cap_pct / 100;
    const result = Math.min(converted, 100);
    return result;
}
/**
 * Converts frontend pub cap percentage to service format (integer format)
 * Frontend uses decimal percentages (e.g., 100 = 100%)
 * Backend stores percentages as integers multiplied by 100 (e.g., 10000 = 100%)
 *
 * @param pubCapPct - Frontend pub cap percentage (decimal format)
 * @returns Service pub cap percentage (integer format)
 *
 * @example
 * pubCapPctToServicePubCap(100) // returns 10000
 * pubCapPctToServicePubCap(90)  // returns 9000
 * pubCapPctToServicePubCap(0)   // returns 0
 * pubCapPctToServicePubCap(undefined) // returns undefined
 */
export function pubCapPctToServicePubCap(pubCapPct) {
    if (pubCapPct === undefined || pubCapPct === null) {
        return undefined;
    }
    return (pubCapPct || 0) * 100;
}
/**
 * Returns an array of bundle objects to be included based on the provided bundle rules and explicitly included bundles.
 *
 *  Combines bundles from `bundlesRules` where `excluded` is `false` and all bundles from `includedBundles`.
 *  Each bundle is represented as an object with a `bundle_id` property.
 *
 * @param bundlesRules - Array of bundle rule objects from `PublisherSetting["bundleRules"]`. Defaults to an empty array.
 * @param includedBundles - Array of explicitly included bundle objects from `PublisherSetting["includedBundles"]`. Defaults to an empty array.
 * @returns An array of objects, each containing a `bundle_id` property.
 */
export const getIncludedBundles = (bundlesRules = [], includedBundles = []) => [
    ...bundlesRules.filter((r) => r.excluded === false).map((r) => ({ bundle_id: r.id })),
    ...includedBundles.map((b) => ({ bundle_id: b.id }))
];
/**
 * Returns an array of channel objects included based on the provided channel rules and included channels.
 *
 * Combines channels from `channelRules` where `excluded` is `false` and all channels from `includedChannels`.
 * Each channel is represented as an object with a `channel_name` property.
 *
 * @param channelRules - Array of channel rule objects from `PublisherSetting["channelRules"]`. Defaults to an empty array.
 * @param includedChannels - Array of channel objects from `PublisherSetting["includedChannels"]`. Defaults to an empty array.
 * @returns An array of objects, each containing a `channel_name` property.
 */
export const getIncludedChannels = (channelRules = [], includedChannels = []) => [
    ...channelRules.filter((r) => r.excluded === false).map((r) => ({ channel_name: r.name })),
    ...includedChannels.map((c) => ({ channel_name: c.name }))
];
//# sourceMappingURL=utils.js.map